import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Search, Trash2, Edit, AlertCircle, User, Mail, Calendar, Shield, Key, ToggleLeft, ToggleRight, Clock
} from 'lucide-react';
import toast from 'react-hot-toast';
import memberService from '../services/memberService';
import roleService from '../services/roleService';
import permissionService from '../services/permissionService';
import ResponsiveModal, { ResponsiveButton } from './ResponsiveModal';
import ResponsiveDataTable from './ResponsiveDataTable';
import EnhancedDataTable from './EnhancedDataTableV2';
import CommonLoader from './CommonLoader';

const DataroomMembers = ({ dataroomUuid, isUserContext = false }) => {
  const [members, setMembers] = useState([]);
  const [summary, setSummary] = useState({ total: 0, active: 0, inactive: 0 });
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' for all, 1 for active, 0 for inactive
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  });

  // Modal states
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [showEditPermissionsModal, setShowEditPermissionsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form states
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  useEffect(() => {
    if (dataroomUuid) {
      fetchMembers();
      fetchRoles();
      fetchPermissions();
    }
  }, [dataroomUuid, pagination.current_page, searchTerm, roleFilter, statusFilter]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current_page,
        per_page: pagination.per_page,
        ...(searchTerm && { search: searchTerm }),
        ...(roleFilter && { role_id: roleFilter }),
        ...(statusFilter !== '' && { is_active: statusFilter })
      };

      const response = isUserContext
        ? await memberService.adminDataroom.list(dataroomUuid, params)
        : await memberService.superAdminDataroom.list(dataroomUuid, params);
      
      // Handle response structure
      if (response) {
        setMembers(response.members || []);
        setSummary(response.summary || { total: 0, active: 0, inactive: 0 });
        
        // Handle pagination
        if (response.pagination) {
          setPagination(response.pagination);
        }
      }
    } catch (error) {
      
      if (error.response?.status === 404) {
        toast.error('Members endpoint not found. Please check API configuration.');
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized. Please check your permissions.');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to load members. Please try again.');
      }
      
      setMembers([]);
      setPagination({
        current_page: 1,
        per_page: 15,
        total: 0,
        last_page: 1
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = isUserContext
        ? await roleService.dataroom.list(dataroomUuid)
        : await roleService.superAdminDataroom.list(dataroomUuid);
      let rolesData = [];
      if (response) {
        if (response.roles && Array.isArray(response.roles)) {
          rolesData = response.roles;
        } else if (response.data && Array.isArray(response.data)) {
          rolesData = response.data;
        } else if (Array.isArray(response)) {
          rolesData = response;
        }
      }
      setRoles(rolesData);
    } catch (error) {
      setRoles([]);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await permissionService.list();
      let permissionsData = [];
      if (response) {
        if (response.permissions && Array.isArray(response.permissions)) {
          permissionsData = response.permissions;
        } else if (response.data && Array.isArray(response.data)) {
          permissionsData = response.data;
        } else if (Array.isArray(response)) {
          permissionsData = response;
        }
      }
      setPermissions(permissionsData);
    } catch (error) {
      setPermissions([]);
    }
  };

  const handleEditRole = (member) => {
    if (!member) {
      toast.error('Invalid member data');
      return;
    }
    setSelectedMember(member);
    setSelectedRoleId(member.role_id || member.dataroom_role_id || '');
    setShowEditRoleModal(true);
  };

  const handleEditPermissions = (member) => {
    if (!member) {
      toast.error('Invalid member data');
      return;
    }
    setSelectedMember(member);
    
    // Extract permission IDs from different possible sources
    let permissionIds = [];
    
    // 1. Check if member has custom_permissions (array of IDs)
    if (member.custom_permissions && Array.isArray(member.custom_permissions)) {
      permissionIds = member.custom_permissions.filter(id => id !== null && id !== undefined);
    }
    // 2. Check if member has a role with permissions
    else if (member.role && member.role.permissions && Array.isArray(member.role.permissions)) {
      permissionIds = member.role.permissions.map(perm => {
        if (typeof perm === 'object' && perm !== null) {
          return perm.id || perm.permission_id || perm.uuid;
        }
        return perm;
      }).filter(id => id !== null && id !== undefined);
    }
    // 3. Check direct permissions property (fallback)
    else if (member.permissions && Array.isArray(member.permissions)) {
      permissionIds = member.permissions.map(perm => {
        if (typeof perm === 'object' && perm !== null) {
          return perm.id || perm.permission_id || perm.uuid;
        }
        return perm;
      }).filter(id => id !== null && id !== undefined);
    }
    
    setSelectedPermissions(permissionIds);
    setShowEditPermissionsModal(true);
  };

  const handleRemoveMember = (member) => {
    if (!member) {
      toast.error('Invalid member data');
      return;
    }
    setSelectedMember(member);
    setShowDeleteModal(true);
  };

  const handleToggleStatus = async (member) => {
    if (!member) {
      toast.error('Invalid member data');
      return;
    }
    
    const userId = member.user_id || member.id;
    // Use is_active_text directly
    const currentStatus = member.is_active_text === 'Active';
    
    try {
      await (isUserContext
        ? memberService.adminDataroom.toggleUserStatus(dataroomUuid, userId)
        : memberService.superAdminDataroom.toggleUserStatus(dataroomUuid, userId));
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to toggle user status');
    }
  };

  const handleSubmitRoleUpdate = async (e) => {
    e.preventDefault();
    
    if (!selectedRoleId) {
      toast.error('Please select a role');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await (isUserContext
        ? memberService.adminDataroom.updateRole(dataroomUuid, selectedMember.user_id || selectedMember.id, selectedRoleId)
        : memberService.superAdminDataroom.updateRole(dataroomUuid, selectedMember.user_id || selectedMember.id, selectedRoleId));
      toast.success('Member role updated successfully');
      setShowEditRoleModal(false);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update member role');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitPermissionsUpdate = async (e) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    
    try {
      await (isUserContext
        ? memberService.adminDataroom.updatePermissions(dataroomUuid, selectedMember.user_id || selectedMember.id, selectedPermissions)
        : memberService.superAdminDataroom.updatePermissions(dataroomUuid, selectedMember.user_id || selectedMember.id, selectedPermissions));
      toast.success('Member permissions updated successfully');
      setShowEditPermissionsModal(false);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update member permissions');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmRemove = async () => {
    if (!selectedMember) {
      toast.error('No member selected');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await (isUserContext
        ? memberService.adminDataroom.remove(dataroomUuid, selectedMember.user_id || selectedMember.id)
        : memberService.superAdminDataroom.remove(dataroomUuid, selectedMember.user_id || selectedMember.id));
      toast.success('Member removed successfully');
      setShowDeleteModal(false);
      setSelectedMember(null);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove member');
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePermission = (permissionId) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(id => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const columns = [
    {
      key: 'user',
      header: 'Member Details',
      sortable: true,
      render: (value, member) => {
        const user = member.user || member;
        if (!user) return <span className="text-gray-400">N/A</span>;
        return (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">
                {user.first_name || user.name} {user.last_name || ''}
              </p>
              <p className="text-sm text-gray-500">{user.email}</p>
              {user.phone_no && (
                <p className="text-xs text-gray-400 mt-1">📱 {user.phone_no}</p>
              )}
            </div>
          </div>
        );
      }
    },
    {
      key: 'role',
      header: 'Role & Permissions',
      sortable: false,
      render: (value, member) => {
        // Check if member is null or undefined
        if (!member) {
          return <span className="text-gray-400 text-sm">No data</span>;
        }
        const role = member.role || member.dataroom_role;
        
        if (role) {
          return (
            <div>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                <Shield className="w-3 h-3 mr-1" />
                {role.display_name || role.name || 'Member'}
              </span>
              {role.permissions && role.permissions.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs text-gray-500 mb-1">
                    Permissions ({role.permissions.length}):
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {role.permissions.slice(0, 3).map(perm => (
                      <span key={perm.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                        {perm.display_name || perm.name}
                      </span>
                    ))}
                    {role.permissions.length > 3 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                        +{role.permissions.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        }
        
        if (member.custom_permissions && member.custom_permissions.length > 0) {
          return (
            <div>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                <Shield className="w-3 h-3 mr-1" />
                Custom Permissions
              </span>
              <div className="mt-1 text-xs text-gray-500">
                {member.custom_permissions.length} permissions
              </div>
            </div>
          );
        }
        
        return <span className="text-gray-400 text-sm">No role assigned</span>;
      }
    },
    {
      key: 'dates',
      header: 'Activity',
      sortable: true,
      render: (value, member) => {
        if (!member) {
          return <span className="text-gray-400 text-sm">No data</span>;
        }
        const joinedDate = member?.joined_at || member?.created_at;
        const lastLogin = member?.user?.last_login_at;
        const formatDate = (dateStr) => {
          if (!dateStr) return 'N/A';
          try {
            if (typeof dateStr === 'string' && dateStr.includes(' ')) {
              const [date] = dateStr.split(' ');
              return date;
            }
            return new Date(dateStr).toLocaleDateString();
          } catch {
            return dateStr;
          }
        };
        
        return (
          <div className="space-y-1">
            {joinedDate && (
              <div className="flex items-center gap-2">
                <Calendar className="w-3 h-3 text-gray-400" />
                <span className="text-xs text-gray-600">
                  Joined: {formatDate(joinedDate)}
                </span>
              </div>
            )}
            {lastLogin && (
              <div className="flex items-center gap-2">
                <Clock className="w-3 h-3 text-gray-400" />
                <span className="text-xs text-gray-500">
                  Last login: {formatDate(lastLogin)}
                </span>
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (value, member) => {
        if (!member) {
          return <span className="text-gray-400 text-sm">No data</span>;
        }
        // Display is_active_text directly
        const statusText = member.is_active_text;
        const isActive = statusText === 'Active';
        
        return (
          <div className="space-y-1">
            <button
              onClick={() => handleToggleStatus(member)}
              className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                isActive
                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                  : 'bg-red-100 text-red-800 hover:bg-red-200'
              }`}
              title={`Click to ${isActive ? 'deactivate' : 'activate'}`}
            >
              {isActive ? (
                <ToggleRight className="w-3.5 h-3.5 mr-1.5" />
              ) : (
                <ToggleLeft className="w-3.5 h-3.5 mr-1.5" />
              )}
              {statusText}
            </button>
            {member.invitation_id && (
              <div className="text-xs text-gray-500">
                Invite ID: #{member.invitation_id}
              </div>
            )}
          </div>
        );
      }
    }
  ];

  const actions = (member) => {
    return [
      {
        icon: Shield,
        label: 'Edit Role',
        onClick: () => handleEditRole(member),
        className: 'text-blue-600 hover:text-blue-800'
      },
      {
        icon: Key,
        label: 'Edit Permissions',
        onClick: () => handleEditPermissions(member),
        className: 'text-green-600 hover:text-green-800'
      },
      {
        icon: Trash2,
        label: 'Remove',
        onClick: () => handleRemoveMember(member),
        className: 'text-red-600 hover:text-red-800'
      }
    ];
  };

  if (loading && members.length === 0) {
    return <CommonLoader />;
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Page Header */}
        <div className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Dataroom Members</h2>
              <p className="mt-1 text-sm text-gray-600">
                Manage all members with access to this dataroom
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Users className="w-5 h-5" />
              <span className="font-medium">{pagination.total || 0} Total Members</span>
            </div>
          </div>
        </div>

        {/* Content Card */}
        <div className="px-6 py-4">

          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between mb-6">
            <div className="flex flex-1 gap-4">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Role Filter */}
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
              >
                <option value="">All Roles</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.display_name || role.name}
                  </option>
                ))}
              </select>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPagination(prev => ({ ...prev, current_page: 1 }));
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
              >
                <option value="">All Status</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
          </div>

          {/* Members Table */}
          <EnhancedDataTable
            data={members}
            columns={columns}
            actions={actions}
            loading={loading}
            emptyMessage="No members found in this dataroom"
            emptyIcon={Users}
            showColumnControls={true}
            enableColumnResize={true}
            enableDragDrop={true}
            onSort={(key, direction) => {
              const sorted = [...members].sort((a, b) => {
                let aVal, bVal;
                
                if (key === 'user') {
                  aVal = `${a.user?.first_name || a.user?.name || ''} ${a.user?.last_name || ''}`;
                  bVal = `${b.user?.first_name || b.user?.name || ''} ${b.user?.last_name || ''}`;
                } else if (key === 'dates') {
                  aVal = a.joined_at || a.created_at || '';
                  bVal = b.joined_at || b.created_at || '';
                  aVal = aVal ? new Date(aVal).getTime() : 0;
                  bVal = bVal ? new Date(bVal).getTime() : 0;
                } else if (key === 'status') {
                  aVal = a.is_active_text === 'Active' ? 1 : 0;
                  bVal = b.is_active_text === 'Active' ? 1 : 0;
                } else {
                  aVal = a[key];
                  bVal = b[key];
                }
                
                if (aVal < bVal) return direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return direction === 'asc' ? 1 : -1;
                return 0;
              });
              setMembers(sorted);
            }}
          />
          
          
          {/* Pagination */}
          {pagination.last_page > 1 && (
            <div className="mt-6 px-6 py-4 bg-white border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
                {pagination.total} members
              </div>
              <div className="flex items-center gap-1">
                {/* First page button */}
                <button
                  onClick={() => setPagination(prev => ({ ...prev, current_page: 1 }))}
                  disabled={pagination.current_page === 1}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                  title="First page"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
                
                {/* Previous page button */}
                <button
                  onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
                  disabled={pagination.current_page === 1}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                  title="Previous page"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                {/* Page numbers */}
                {(() => {
                  const pages = [];
                  const maxPages = Math.min(5, pagination.last_page);
                  let startPage = 1;
                  
                  if (pagination.last_page <= 5) {
                    startPage = 1;
                  } else if (pagination.current_page <= 3) {
                    startPage = 1;
                  } else if (pagination.current_page >= pagination.last_page - 2) {
                    startPage = pagination.last_page - 4;
                  } else {
                    startPage = pagination.current_page - 2;
                  }
                  
                  for (let i = 0; i < maxPages; i++) {
                    const pageNum = startPage + i;
                    pages.push(
                      <button
                        key={pageNum}
                        onClick={() => setPagination(prev => ({ ...prev, current_page: pageNum }))}
                        className={`w-8 h-8 flex items-center justify-center rounded text-sm font-medium transition-all ${
                          pageNum === pagination.current_page
                            ? 'text-white'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                        style={pageNum === pagination.current_page ? { backgroundColor: '#3872FA' } : {}}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                  return pages;
                })()}
                
                {/* Next page button */}
                <button
                  onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
                  disabled={pagination.current_page === pagination.last_page}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                  title="Next page"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                
                {/* Last page button */}
                <button
                  onClick={() => setPagination(prev => ({ ...prev, current_page: pagination.last_page }))}
                  disabled={pagination.current_page === pagination.last_page}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                  title="Last page"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Role Modal */}
      <ResponsiveModal
        isOpen={showEditRoleModal}
        onClose={() => {
          setShowEditRoleModal(false);
          setSelectedRoleId('');
        }}
        title="Edit Member Role"
      >
        <form onSubmit={handleSubmitRoleUpdate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Member
            </label>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <User className="w-5 h-5 text-gray-500" />
              <div>
                <p className="font-medium text-gray-900">
                  {selectedMember?.user_name || selectedMember?.name}
                </p>
                <p className="text-sm text-gray-500">
                  {selectedMember?.user_email || selectedMember?.email}
                </p>
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Role <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">Choose a role...</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>
                  {role.name || role.display_name}
                  {role.description && ` - ${role.description}`}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex gap-3 pt-4">
            <ResponsiveButton
              type="submit"
              variant="primary"
              loading={isSubmitting}
            >
              Update Role
            </ResponsiveButton>
            <ResponsiveButton
              type="button"
              variant="secondary"
              onClick={() => {
                setShowEditRoleModal(false);
                setSelectedRoleId('');
              }}
            >
              Cancel
            </ResponsiveButton>
          </div>
        </form>
      </ResponsiveModal>

      {/* Edit Permissions Modal */}
      <ResponsiveModal
        isOpen={showEditPermissionsModal}
        onClose={() => {
          setShowEditPermissionsModal(false);
          setSelectedPermissions([]);
        }}
        title="Edit Member Permissions"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Member
            </label>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <User className="w-5 h-5 text-gray-500" />
              <div>
                <p className="font-medium text-gray-900">
                  {selectedMember?.user_name || selectedMember?.name}
                </p>
                <p className="text-sm text-gray-500">
                  {selectedMember?.user_email || selectedMember?.email}
                </p>
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Permissions
            </label>
            <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-lg p-3 space-y-2">
              {permissions.length > 0 ? (
                permissions.map((permission) => (
                  <label key={permission.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedPermissions.includes(permission.id)}
                      onChange={() => togglePermission(permission.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{permission.display_name || permission.name}</span>
                  </label>
                ))
              ) : (
                <p className="text-sm text-gray-500">No permissions available</p>
              )}
            </div>
          </div>
          
          <div className="flex gap-3 pt-4">
            <ResponsiveButton
              variant="primary"
              onClick={handleSubmitPermissionsUpdate}
              loading={isSubmitting}
            >
              Update Permissions
            </ResponsiveButton>
            <ResponsiveButton
              variant="secondary"
              onClick={() => {
                setShowEditPermissionsModal(false);
                setSelectedPermissions([]);
              }}
            >
              Cancel
            </ResponsiveButton>
          </div>
        </div>
      </ResponsiveModal>

      {/* Remove Member Confirmation Modal */}
      <ResponsiveModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Remove Member"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">
              Are you sure you want to remove <strong>{selectedMember?.user_name || selectedMember?.name}</strong> from this dataroom? 
              They will lose all access to the dataroom and its contents.
            </p>
          </div>
          
          <div className="flex gap-3">
            <ResponsiveButton
              variant="danger"
              onClick={handleConfirmRemove}
              loading={isSubmitting}
            >
              Remove Member
            </ResponsiveButton>
            <ResponsiveButton
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </ResponsiveButton>
          </div>
        </div>
      </ResponsiveModal>
    </>
  );
};

export default DataroomMembers;